import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { ChevronDown, ChevronUp, X, Plus, Loader2 } from 'lucide-react';

const MultiSelectCreatable = ({
  userId,                                    // NEW: Required for per-user scoping
  fieldKey,                                  // NEW: Required for field-specific storage
  label,
  options: globalOptions = [],               // RENAMED: Base/global options (read-only)
  value = [],
  onChange,
  placeholder = "Select multiple...",
  disabled = false,
  className = '',
  error,
  required = false,
  allowCreate = true,
  maxSelected,
  disabledValues = [],
  normalizeNewOption,                        // NEW: Custom normalization function
  computeNewValue,                          // NEW: Custom value computation function
  validateNewOption,                        // NEW: Validation function
  getOptionValue = (opt) => opt?.value,
  getOptionLabel = (opt) => opt?.label
}) => {
  // NEW: Per-user localStorage storage
  const storageKey = `ms:${fieldKey}:${userId}`;
  
  // Add missing state declarations
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isCreating, setIsCreating] = useState(false);
  const [validationError, setValidationError] = useState('');
  
  // Add missing refs
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  
  // NEW: User-specific options from localStorage
  const [userOptions, setUserOptions] = useState(() => {
    if (!userId || !fieldKey) return [];
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Default normalization function
  const defaultNormalizeNewOption = useCallback((raw) => {
    return raw?.trim()
      ?.replace(/\s+/g, ' ')
      ?.replace(/\w\S*/g, (txt) => 
        txt?.charAt(0)?.toUpperCase() + txt?.substr(1)?.toLowerCase()
      );
  }, []);

  // NEW: Default value computation (kebab-case)
  const defaultComputeNewValue = useCallback((label) => {
    return label
      ?.toLowerCase()
      ?.replace(/[^a-z0-9\s]/g, '') // Remove special chars
      ?.replace(/\s+/g, '-')        // Replace spaces with dashes
      ?.replace(/^-+|-+$/g, '');    // Remove leading/trailing dashes
  }, []);

  // NEW: Merge global and user options with case-insensitive uniqueness
  const uniqueBy = useCallback((array, keyFn) => {
    const seen = new Set();
    return array?.filter(item => {
      const key = keyFn(item)?.toLowerCase();
      if (seen?.has(key)) return false;
      seen?.add(key);
      return true;
    });
  }, []);

  // NEW: All options (global + user-specific) with deduplication
  const allOptions = uniqueBy(
    [...(globalOptions || []), ...(userOptions || [])], 
    getOptionValue
  );

  // Filter options based on search query
  const filteredOptions = allOptions?.filter(option => {
    const label = getOptionLabel(option)?.toLowerCase();
    const query = searchQuery?.toLowerCase();
    return label?.includes(query);
  });

  // NEW: Improved create option logic with case-insensitive checking
  const showCreateOption = allowCreate && 
    searchQuery?.trim() && 
    searchQuery?.trim()?.length > 0 && 
    !allOptions?.some(opt => {
      const optionLabel = getOptionLabel(opt)?.toLowerCase()?.trim();
      const optionValue = getOptionValue(opt)?.toLowerCase()?.trim();
      const normalizedQuery = (normalizeNewOption || defaultNormalizeNewOption)(searchQuery)?.toLowerCase()?.trim();
      const computedValue = (computeNewValue || defaultComputeNewValue)(normalizedQuery)?.toLowerCase()?.trim();
      
      return optionLabel === normalizedQuery || optionValue === computedValue;
    });

  // Get selected options objects
  const selectedOptions = allOptions?.filter(option => 
    value?.includes(getOptionValue(option))
  ) || [];

  // Check if max selected is reached
  const isMaxSelectedReached = maxSelected && selectedOptions?.length >= maxSelected;

  // Handle option selection/deselection
  const handleOptionToggle = useCallback((optionValue) => {
    if (!onChange || disabledValues?.includes(optionValue)) return;
    
    const newValue = value?.includes(optionValue)
      ? value?.filter(v => v !== optionValue)
      : isMaxSelectedReached 
        ? value 
        : [...value, optionValue];
    
    onChange(newValue);
  }, [onChange, value, disabledValues, isMaxSelectedReached]);

  // Remove individual chip
  const handleRemoveChip = useCallback((optionValue, e) => {
    e?.stopPropagation();
    if (!onChange) return;
    
    const newValue = value?.filter(v => v !== optionValue);
    onChange(newValue);
    
    // Announce removal for accessibility
    const announcement = document.createElement('div');
    announcement?.setAttribute('aria-live', 'polite');
    announcement?.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    const removedOption = allOptions?.find(opt => getOptionValue(opt) === optionValue);
    announcement.textContent = `Removed ${getOptionLabel(removedOption)}`;
    document.body?.appendChild(announcement);
    setTimeout(() => {
      if (document.body?.contains(announcement)) {
        document.body?.removeChild(announcement);
      }
    }, 1000);
  }, [onChange, value, allOptions, getOptionValue, getOptionLabel]);

  // Clear all selections
  const handleClearAll = useCallback((e) => {
    e?.stopPropagation();
    if (!onChange) return;
    onChange([]);
  }, [onChange]);

  // NEW: Persist user options to localStorage
  const persistUserOptions = useCallback((newUserOptions) => {
    if (!userId || !fieldKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(newUserOptions));
    } catch (error) {
      console.error('Failed to persist user options:', error);
    }
  }, [storageKey, userId, fieldKey]);

  // Handle creating new option with reliable onMouseDown
  const handleCreateOption = useCallback(async (query) => {
    if (!allowCreate || !query?.trim() || isCreating || !userId || !fieldKey) return;

    const normalizeFunction = normalizeNewOption || defaultNormalizeNewOption;
    const computeFunction = computeNewValue || defaultComputeNewValue;
    
    const normalizedLabel = normalizeFunction(query);
    
    // Validation
    if (!normalizedLabel || normalizedLabel?.trim()?.length === 0) {
      setValidationError('Option name cannot be empty');
      return;
    }
    
    if (validateNewOption) {
      const validationResult = validateNewOption(query);
      if (validationResult) {
        setValidationError(validationResult);
        return;
      }
    }

    // Clear validation error
    setValidationError('');

    try {
      setIsCreating(true);
      
      const computedValue = computeFunction(normalizedLabel);
      const finalValue = computedValue || `custom-${Date.now()}`;
      
      const newOption = {
        value: finalValue,
        label: normalizedLabel,
        scope: 'user' // Mark as user-created
      };

      // NEW: Case-insensitive duplicate checking
      const existingByValue = allOptions?.find(opt => 
        getOptionValue(opt)?.toLowerCase()?.trim() === finalValue?.toLowerCase()?.trim()
      );
      
      const existingByLabel = allOptions?.find(opt => 
        getOptionLabel(opt)?.toLowerCase()?.trim() === normalizedLabel?.toLowerCase()?.trim()
      );
      
      if (existingByValue || existingByLabel) {
        // Option already exists, just select it
        const existingOption = existingByValue || existingByLabel;
        const existingValue = getOptionValue(existingOption);
        
        if (!value?.includes(existingValue) && !isMaxSelectedReached) {
          onChange([...value, existingValue]);
        }
        setValidationError('');
      } else {
        // NEW: Add to userOptions and persist
        const updatedUserOptions = [...userOptions, newOption];
        setUserOptions(updatedUserOptions);
        persistUserOptions(updatedUserOptions);
        
        // Select the new option immediately
        if (!isMaxSelectedReached) {
          onChange([...value, finalValue]);
        }

        // Announce creation for accessibility
        const announcement = document.createElement('div');
        announcement?.setAttribute('aria-live', 'polite');
        announcement?.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = `Created and selected ${normalizedLabel}`;
        document.body?.appendChild(announcement);
        setTimeout(() => {
          if (document.body?.contains(announcement)) {
            document.body?.removeChild(announcement);
          }
        }, 1000);
      }

      // NEW: Keep menu open, clear query, maintain focus
      setSearchQuery('');
      setFocusedIndex(-1);
      
      // Keep focus in search input
      setTimeout(() => {
        if (searchInputRef?.current) {
          searchInputRef?.current?.focus();
        }
      }, 10);
      
    } catch (error) {
      console.error('Error creating option:', error);
      setValidationError('Failed to create option. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [allowCreate, isCreating, userId, fieldKey, normalizeNewOption, defaultNormalizeNewOption, computeNewValue, defaultComputeNewValue, validateNewOption, allOptions, getOptionValue, getOptionLabel, userOptions, persistUserOptions, value, isMaxSelectedReached, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e?.key === 'ArrowDown' || e?.key === 'ArrowUp' || e?.key === 'Enter' || e?.key === ' ') {
        e?.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
        setTimeout(() => searchInputRef?.current?.focus(), 10);
      }
      return;
    }

    const totalOptions = filteredOptions?.length + (showCreateOption ? 1 : 0);

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setFocusedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : totalOptions - 1
        );
        break;
      case 'Enter':
        e?.preventDefault();
        if (showCreateOption && focusedIndex === filteredOptions?.length) {
          // NEW: Create new option on Enter
          handleCreateOption(searchQuery);
        } else if (focusedIndex >= 0 && focusedIndex < filteredOptions?.length) {
          // Toggle existing option
          handleOptionToggle(getOptionValue(filteredOptions?.[focusedIndex]));
        }
        break;
      case 'Escape':
        e?.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        setSearchQuery('');
        setValidationError('');
        inputRef?.current?.focus();
        break;
      default:
        break;
    }
  }, [isOpen, filteredOptions, showCreateOption, focusedIndex, handleCreateOption, searchQuery, handleOptionToggle, getOptionValue]);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    setValidationError('');
    setFocusedIndex(0);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
        setSearchQuery('');
        setValidationError('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll focused item into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listRef?.current) {
      const focusedElement = listRef?.current?.children?.[focusedIndex];
      if (focusedElement) {
        focusedElement?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [focusedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      setTimeout(() => searchInputRef?.current?.focus(), 10);
    }
  }, [isOpen]);

  return (
    <div className={cn("relative w-full", className)}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-text mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Main Input Container */}
      <div className="relative" ref={dropdownRef}>
        <div
          ref={inputRef}
          className={cn(
            // Base styles with theme-aware colors
            "min-h-[40px] w-full rounded-md border bg-surface px-3 py-2",
            "cursor-pointer transition-colors duration-200",
            // Focus and hover states
            "focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent",
            "hover:border-muted",
            // Error state
            error || validationError ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-border",
            // Disabled state
            disabled && "cursor-not-allowed opacity-50 bg-muted/20"
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={label || "Multi-select dropdown"}
        >
          <div className="flex flex-wrap items-center gap-1 pr-8">
            {/* Selected Chips/Badges */}
            {selectedOptions?.length > 0 ? (
              selectedOptions?.map((option) => (
                <span
                  key={getOptionValue(option)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-accent/10 text-accent rounded-md border border-accent/20"
                >
                  {getOptionLabel(option)}
                  <button
                    type="button"
                    onClick={(e) => handleRemoveChip(getOptionValue(option), e)}
                    className="text-accent hover:text-accent/80 focus:outline-none"
                    aria-label={`Remove ${getOptionLabel(option)}`}
                    tabIndex={-1}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-muted text-sm">{placeholder}</span>
            )}
          </div>

          {/* Right Side Controls */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {/* Clear Button */}
            {selectedOptions?.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-muted hover:text-text focus:outline-none p-1"
                aria-label="Clear all selections"
                tabIndex={-1}
              >
                <X size={16} />
              </button>
            )}
            
            {/* Dropdown Arrow */}
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted" />
            )}
          </div>
        </div>

        {/* Dropdown List */}
        {isOpen && (
          <div 
            className={cn(
              "absolute z-50 w-full mt-1 bg-surface border border-border rounded-md shadow-popover max-h-60 overflow-auto"
            )}
            role="listbox"
            aria-label={`${label || 'Options'} list`}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-border">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Type to filter or create..."
                className="w-full px-2 py-1 text-sm border border-border bg-surface text-text rounded focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                onKeyDown={handleKeyDown}
                aria-label="Search or filter options"
              />
            </div>

            <div ref={listRef}>
              {/* Existing Options */}
              {filteredOptions?.map((option, index) => {
                const optionValue = getOptionValue(option);
                const isSelected = value?.includes(optionValue);
                const isFocused = index === focusedIndex;
                const isDisabled = disabledValues?.includes(optionValue) || 
                                  (isMaxSelectedReached && !isSelected);
                
                return (
                  <div
                    key={optionValue}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors",
                      // Hover and focus styles with theme-aware colors
                      isFocused && !isDisabled && "bg-accent/10",
                      !isFocused && !isDisabled && "hover:bg-muted/10",
                      // Selected state
                      isSelected && "bg-accent/10 text-accent",
                      // Disabled state
                      isDisabled && "opacity-50 cursor-not-allowed bg-muted/10"
                    )}
                    onClick={() => !isDisabled && handleOptionToggle(optionValue)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled}
                  >
                    {/* Checkbox */}
                    <div 
                      className={cn(
                        "w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
                        isSelected 
                          ? "bg-accent border-accent" 
                          : isDisabled 
                            ? "border-muted bg-muted/20" :"border-border bg-surface"
                      )}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3 text-surface" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    
                    {/* Option Label */}
                    <span className="flex-1 text-text">{getOptionLabel(option)}</span>
                  </div>
                );
              })}

              {/* Create New Option */}
              {showCreateOption && (
                <div
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors",
                    "border-t border-border",
                    focusedIndex === filteredOptions?.length && !isMaxSelectedReached && !isCreating && "bg-accent/10",
                    focusedIndex !== filteredOptions?.length && !isMaxSelectedReached && !isCreating && "hover:bg-muted/10",
                    (isMaxSelectedReached || isCreating) && "opacity-50 cursor-not-allowed"
                  )}
                  onMouseDown={(e) => {
                    e?.preventDefault();
                    if (!isMaxSelectedReached && !isCreating && allowCreate) {
                      handleCreateOption(searchQuery);
                    }
                  }}
                  onMouseEnter={() => setFocusedIndex(filteredOptions?.length)}
                  role="option"
                  aria-selected={false}
                  aria-disabled={isMaxSelectedReached || isCreating}
                >
                  {/* Plus Icon or Spinner */}
                  <div className="w-4 h-4 flex items-center justify-center">
                    {isCreating ? (
                      <Loader2 className="w-3 h-3 animate-spin text-accent" />
                    ) : (
                      <Plus className="w-3 h-3 text-accent" />
                    )}
                  </div>
                  
                  {/* Create Label */}
                  <span className="flex-1 text-accent font-medium">
                    {isCreating 
                      ? 'Creating option...' : `Create'${(normalizeNewOption || defaultNormalizeNewOption)(searchQuery)}'`
                    }
                  </span>
                </div>
              )}

              {/* No Results */}
              {filteredOptions?.length === 0 && !showCreateOption && (
                <div className="px-3 py-2 text-sm text-muted text-center">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Error Messages */}
      {(error || validationError) && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error || validationError}
        </p>
      )}
      
      {/* Max Selected Info */}
      {maxSelected && selectedOptions?.length >= maxSelected && (
        <p className="mt-1 text-sm text-amber-600" role="status">
          Maximum {maxSelected} selections allowed
        </p>
      )}
    </div>
  );
};

export default MultiSelectCreatable;